import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../api.js";
import {
  customRange,
  donutArcs,
  presetRange,
  RANGE_PRESETS,
  sharePercent
} from "../chart-model.js";
import {
  joinOrders,
  revenueFor,
  orderValueMinor,
  type CallWithOrder,
  formatDuration,
  formatMinor,
  formatStartedAt,
  groupByDay,
  outcomeLabel,
  outcomeTone,
  formatClock,
  billingRuleText,
  formatTotalTime,
  staffTimeSavedSeconds
} from "../calls-model.js";
import { TranscriptDialog } from "./TranscriptDialog.js";
import {
  BagIcon,
  CalendarIcon,
  ClockIcon,
  CallOutcomeIcon,
  CoinIcon,
  Hint,
  MessageIcon,
  PhoneIcon,
  TimerIcon,
  UserIcon,
  WithIcon
} from "../icons.js";
import { pluralize, todayInput, usePageTitle } from "../ui.js";

/**
 * Calls and charges for a month, and the transcript of any one call.
 *
 * The transcript is the reason this page exists at this stage: reading agent speech and tool
 * calls interleaved is what has caught nearly every real defect so far, and doing it from
 * CloudWatch requires an AWS login and a log-group name.
 */
export function CallsPage() {
  const { restaurantId = "" } = useParams();
  usePageTitle("Calls");
  const [preset, setPreset] = useState("7d");
  const [custom, setCustom] = useState({ from: todayInput(), to: todayInput() });
  /**
   * MUST be memoised. presetRange() calls new Date(), so recomputing it on every render produced
   * a fresh `until` each time — which changed the query keys, which refetched, which re-rendered.
   * The page sat in an infinite loop hammering all three APIs several times a second.
   */
  const range = useMemo(
    () =>
      preset === "custom"
        ? customRange(custom.from, custom.to)
        : presetRange(RANGE_PRESETS.find((p) => p.key === preset)?.days ?? 7),
    [preset, custom.from, custom.to]
  );
  const [openCall, setOpenCall] = useState<CallWithOrder | null>(null);

  const calls = useQuery({
    queryKey: ["calls", restaurantId, range.since, range.until],
    queryFn: () => api.listCalls(restaurantId, range)
  });
  const billing = useQuery({
    queryKey: ["billing", restaurantId, range.since, range.until],
    queryFn: () => api.getBilling(restaurantId, range)
  });
  // The order is the single source of truth for what it was worth, so we join on orderId
  // instead of billing keeping a copy. Same query key the Orders page uses, so it's shared.
  const orders = useQuery({
    queryKey: ["orders", restaurantId, range.since, range.until],
    queryFn: () => api.listOrders(restaurantId, { since: range.since, until: range.until, limit: 200 })
  });

  const summary = billing.data?.summary;
  const withOrders = joinOrders(calls.data?.calls ?? [], orders.data?.orders ?? []);
  const revenue = revenueFor(withOrders, summary?.billedMinor ?? 0);
  const grouped = groupByDay(withOrders);

  return (
    <>
      <div className="page-head">
        <h1>Calls</h1>
        <div className="page-actions range-picker">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={preset === p.key ? "chip on" : "chip"}
              onClick={() => setPreset(p.key)}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={preset === "custom" ? "chip on" : "chip"}
            onClick={() => setPreset("custom")}
          >
            <WithIcon icon={<CalendarIcon size={15} />}>Custom</WithIcon>
          </button>
          {preset === "custom" ? (
            <span className="custom-range">
              <input
                type="date"
                value={custom.from}
                max={custom.to}
                onChange={(e) => setCustom({ ...custom, from: e.target.value })}
                aria-label="From date"
              />
              <span className="muted">to</span>
              <input
                type="date"
                value={custom.to}
                min={custom.from}
                max={todayInput()}
                onChange={(e) => setCustom({ ...custom, to: e.target.value })}
                aria-label="To date"
              />
            </span>
          ) : null}
        </div>
      </div>

      {summary ? (
        <div className="calls-overview">
          {/* Four numbers, not nine: what it cost, what it produced, the ratio, and the staff time it saved.
              Everything else was derivable and just made the page hard to read. */}
          <div className="headline">
            <Stat icon={<CoinIcon />} label="Cost" value={formatMinor(summary.billedMinor)} strong />
            <Stat
              icon={<BagIcon />}
              label="Order value"
              value={
                revenue.ordersValued === 0 && summary.ordersPlaced > 0
                  ? "—"
                  : formatMinor(revenue.orderRevenueMinor)
              }
              strong
            />
            {revenue.returnMultiple !== undefined && revenue.returnMultiple > 0 ? (
              <Stat
                icon={<CoinIcon />}
                label="Back per $1"
                value={`$${revenue.returnMultiple.toFixed(0)}`}
                strong
              />
            ) : null}
            <Stat
              icon={<ClockIcon />}
              label="Staff time saved"
              value={formatTotalTime(staffTimeSavedSeconds(calls.data?.calls ?? []))}
              hint="Time the agent spent on the phone so your staff didn't have to: the length of every call it answered, not counting calls that failed on our side."
              strong
            />
          </div>

          {/* Outcomes are parts of a whole, which is the one thing a pie is actually for. */}
          <OutcomeDonut calls={withOrders} />
        </div>
      ) : null}

      {summary ? (
        <p className="muted small facts-line">
          <WithIcon icon={<PhoneIcon size={15} />}>{pluralize(summary.calls, "call")}</WithIcon>
          <WithIcon icon={<TimerIcon size={15} />}>{formatClock(summary.billableSeconds)} billed</WithIcon>
          <Hint label="How calls are billed">
            {billingRuleText(summary)}. Calls that failed on our side aren’t charged.
          </Hint>
        </p>
      ) : null}

      {calls.isLoading ? <p className="muted">Loading calls…</p> : null}
      {calls.error ? (
        <div className="notice error">
          {calls.error instanceof ApiError ? calls.error.message : String(calls.error)}
        </div>
      ) : null}

      {calls.data && grouped.length === 0 ? (
        <p className="muted">No calls in this period.</p>
      ) : null}

      {grouped.map((group) => (
        <section key={group.day} className="call-day">
          <h2 className="section-title">{group.day}</h2>
          <ul className="call-list">
            <li className="call-row call-head" aria-hidden="true">
              <span className="call-main">
                <span />
                <span />
                <span />
                <span className="muted small">length</span>
                <span className="muted small">cost</span>
                <span className="muted small">order value</span>
              </span>
            </li>
            {group.calls.map((c) => (
              <li key={c.callSid} className="call-row">
                <button className="call-main" onClick={() => setOpenCall(c)}>
                  <span className={`badge tone-${outcomeTone(c.outcome)}`}>
                    <WithIcon icon={<CallOutcomeIcon outcome={c.outcome} size={14} />}>
                      {outcomeLabel(c.outcome)}
                    </WithIcon>
                  </span>
                  <span className="call-when">{formatStartedAt(c.startedAtIso)}</span>
                  <span className="muted">
                    <WithIcon icon={<UserIcon size={14} />}>{c.fromNumber ?? "Unknown"}</WithIcon>
                  </span>
                  {/* Real length beside billed time, so the rounding is visible rather
                      than looking like an error. */}
                  <span className="muted" title={`Billed ${formatClock(c.billableSeconds)}`}>
                    <WithIcon icon={<TimerIcon size={14} />}>{formatDuration(c.durationSeconds)}</WithIcon>
                  </span>
                  <span className="call-charge">{formatMinor(c.billedMinor)}</span>
                  <span className={orderValueMinor(c) !== undefined ? "call-revenue" : "call-revenue none"}>
                    {orderValueMinor(c) !== undefined ? formatMinor(orderValueMinor(c)!) : "—"}
                  </span>
                </button>
                {/* A message needs a person to act on it, so it sits in the row rather than
                    hidden behind the transcript. */}
                {c.message ? (
                  <p className="call-message">
                    <WithIcon icon={<MessageIcon size={15} />}>{c.message}</WithIcon>
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ))}

      {openCall ? (
        <TranscriptDialog
          restaurantId={restaurantId}
          call={openCall}
          onClose={() => setOpenCall(null)}
        />
      ) : null}
    </>
  );
}

/** Colours match the outcome badges elsewhere, so the chart and the rows agree. */
const OUTCOME_COLORS: Record<string, string> = {
  ORDER_PLACED: "#1c6b32",
  RESERVED: "#5b3a9b",
  ABANDONED: "#8a9099",
  TRANSFERRED: "#1c4c91",
  FAILED: "#8c2b22"
};

function OutcomeDonut({ calls }: { calls: CallWithOrder[] }) {
  const counts = new Map<string, number>();
  for (const c of calls) counts.set(c.outcome, (counts.get(c.outcome) ?? 0) + 1);

  const arcs = donutArcs(
    [...counts.entries()].map(([outcome, value]) => ({
      outcome,
      label: outcomeLabel(outcome as CallWithOrder["outcome"]),
      value,
      color: OUTCOME_COLORS[outcome] ?? "#8a9099"
    }))
  );

  if (arcs.length === 0) return null;

  return (
    <figure className="donut">
      <svg viewBox="0 0 160 160" width="140" height="140" role="img" aria-label="Call outcomes">
        {arcs.map((a) => (
          <path key={a.label} d={a.path} fill={a.color} />
        ))}
      </svg>
      {/* The legend carries the numbers, so the chart itself needs no labels crowding it. */}
      <figcaption>
        <ul className="legend">
          {arcs.map((a) => (
            <li key={a.label}>
              <span className="swatch" style={{ background: a.color }} aria-hidden="true" />
              <CallOutcomeIcon outcome={a.outcome} size={14} />
              {a.label} <strong>{a.value}</strong>{" "}
              <span className="muted">{sharePercent(a.share)}%</span>
            </li>
          ))}
        </ul>
      </figcaption>
    </figure>
  );
}

function Stat({
  icon,
  label,
  value,
  hint,
  strong
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  strong?: boolean;
}) {
  return (
    <div className={`stat${strong ? " strong" : ""}`}>
      <span className="stat-value">{value}</span>
      <span className="stat-label with-icon">
        {icon}
        <span>{label}</span>
        {hint ? <Hint>{hint}</Hint> : null}
      </span>
    </div>
  );
}
